import { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  User,
  Menu,
  X,
  Crown,
  Shield,
  LayoutDashboard,
  Tag,
  ScrollText,
  Bell,
  Handshake,
  Network,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import SidebarLogo from "./SidebarLogo";
import {
  isProUser,
  shouldShowProMemberChrome,
  shouldShowLaunchAccessChrome,
  LAUNCH_PLAN_NOTE,
  LAUNCH_PLAN_TITLE,
} from "../utils/subscription";
import { useTheme } from "../hooks/useTheme";
import { apiFetch, clearWebSession } from "../lib/webApi";
import { NAV_SECTIONS } from "../lib/navSections";
import { Badge } from "./design-system";
import {
  buildNavSectionOpenState,
  mergeNavSectionOpenForActiveRoute,
} from "../../shared/navSectionCollapse.mjs";
import { useCoachChatAvailable, resetCoachChatAvailabilityCache } from "../hooks/useCoachChatAvailable";
import { useProductDoors, filterNavItemsByDoors } from "../lib/productDoors";
/** Outer chrome grows with the iOS PWA status bar; the inner row stays h-14. */
export const APP_MOBILE_HEADER_SAFE_AREA_CLASS = "pt-[env(safe-area-inset-top,0px)]";
/** Locks `#main-content` overflow. Do not use body `position:fixed` - that jumps by html safe-area padding. */
export const WEB_NAV_OPEN_CLASS = "fcp-web-nav-open";
/** Ignore the opening tap so iOS does not close the drawer on the same touchend. */
export const WEB_NAV_OPEN_IGNORE_MS = 400;

const MOBILE_BREAKPOINT = 768;
export const SIDEBAR_SECTIONS_STORAGE_KEY = "fcp.web.sidebar.sectionsOpen";

function readSavedSectionOpen() {
  try {
    return JSON.parse(localStorage.getItem(SIDEBAR_SECTIONS_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function persistSectionOpen(next) {
  try {
    localStorage.setItem(SIDEBAR_SECTIONS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable */
  }
}

function Sidebar() {
  const { isDark } = useTheme();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const ignoreBackdropCloseUntilRef = useRef(0);
  const [adminOpen, setAdminOpen] = useState(
    location.pathname === "/admin" || location.pathname.startsWith("/admin/")
  );
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
  );
  const [user, setUser] = useState(() =>
    JSON.parse(localStorage.getItem("user") || "{}")
  );
  useEffect(() => {
    const check = () => {
      const m = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(m);
      if (!m) setMobileOpen(false);
    };
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (
      location.pathname === "/admin" ||
      location.pathname.startsWith("/admin/")
    ) {
      setAdminOpen(true);
    }
  }, [location.pathname]);

  // Lock the Layout scrollport only. Body position:fixed jumps by html safe-area
  // padding on iOS standalone / Add to Home Screen.
  useEffect(() => {
    if (!isMobile) return;
    document.documentElement.classList.toggle(WEB_NAV_OPEN_CLASS, mobileOpen);
    return () => {
      document.documentElement.classList.remove(WEB_NAV_OPEN_CLASS);
    };
  }, [mobileOpen, isMobile]);

  // Listen for localStorage changes (subscription updates)
  useEffect(() => {
    const handleStorageChange = () => {
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      setUser(userData);
    };

    // Listen for storage events from other tabs
    window.addEventListener("storage", handleStorageChange);

    // Also check on route change (same tab updates)
    handleStorageChange();

    return () => window.removeEventListener("storage", handleStorageChange);
  }, [location.pathname]);

  const openMobileNav = () => {
    ignoreBackdropCloseUntilRef.current = Date.now() + WEB_NAV_OPEN_IGNORE_MS;
    setMobileOpen(true);
  };

  const closeMobileNavFromBackdrop = () => {
    if (Date.now() < ignoreBackdropCloseUntilRef.current) return;
    setMobileOpen(false);
  };

  const isPro = isProUser(user);
  const showProMemberChrome = shouldShowProMemberChrome(user);
  const showLaunchAccessChrome = shouldShowLaunchAccessChrome(user);
  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPERADMIN";
  const { coachChatAvailable, coachChatChecked } = useCoachChatAvailable();
  const productDoors = useProductDoors();
  const navSections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: filterNavItemsByDoors(section.items, productDoors).filter((item) => {
          if (item.href !== "/coach") return true;
          return coachChatChecked && coachChatAvailable;
        }),
      })),
    [productDoors, coachChatAvailable, coachChatChecked]
  );
  const shellClass = isDark
    ? "bg-[var(--surface-card)]"
    : "bg-[var(--surface-card)] shadow-[var(--shadow-panel)]";
  const topBarClass = isDark
    ? "bg-[var(--surface-card)]"
    : "bg-[var(--surface-card)] shadow-[var(--shadow-sm)]";
  const navTextClass = isDark
    ? "text-stone-400 hover:bg-[var(--surface-raised)] hover:text-white"
    : "text-stone-700 hover:bg-stone-100 hover:text-stone-900";
  const subNavTextClass = isDark
    ? "text-stone-500 hover:bg-[var(--surface-raised)] hover:text-stone-300"
    : "text-stone-600 hover:bg-stone-100 hover:text-stone-900";
  /** Warm tint active state — shadow + callout surface, not ring outline */
  const navActiveClass = isDark
    ? "bg-[var(--surface-callout)] text-[var(--brand-primary)] font-medium shadow-[var(--shadow-sm)]"
    : "bg-[var(--surface-callout)] text-[var(--text-primary)] font-medium shadow-[var(--shadow-sm)]";
  const hairlineBorderColor = isDark
    ? "border-stone-800/80"
    : "border-[color:color-mix(in_srgb,var(--border-subtle)_35%,transparent)]";
  const sectionDividerClass = `border-t ${hairlineBorderColor}`;
  const headerDividerClass = `border-b ${hairlineBorderColor}`;
  const sectionHeaderActiveClass = isDark
    ? "text-[var(--text-primary)] font-semibold"
    : "text-[var(--text-primary)] font-semibold";

  const [sectionOpen, setSectionOpen] = useState(() =>
    buildNavSectionOpenState({
      sections: NAV_SECTIONS,
      pathname: location.pathname,
      saved: typeof localStorage !== "undefined" ? readSavedSectionOpen() : {},
    })
  );

  // Open the section that owns a newly selected route. Do not re-run when
  // navSections gets a new identity on the same path: that snapped a caret
  // collapse back open (active route is always "in" the section).
  const sectionMergePathRef = useRef(location.pathname);
  const navSectionsRef = useRef(navSections);
  navSectionsRef.current = navSections;

  useEffect(() => {
    if (sectionMergePathRef.current === location.pathname) return;
    sectionMergePathRef.current = location.pathname;
    setSectionOpen((prev) => {
      const next = mergeNavSectionOpenForActiveRoute(
        prev,
        navSectionsRef.current,
        location.pathname
      );
      if (next !== prev) persistSectionOpen(next);
      return next;
    });
  }, [location.pathname]);

  const toggleSection = (sectionId) => {
    setSectionOpen((prev) => {
      const next = { ...prev, [sectionId]: !prev[sectionId] };
      persistSectionOpen(next);
      return next;
    });
  };

  const showSectionLabels = isMobile || !isCollapsed;
  const useSectionCollapse = showSectionLabels;

  const adminNavigation = isAdmin
    ? [
        { name: "Dashboard", href: "/admin", icon: LayoutDashboard },
        { name: "Manage Users", href: "/admin/users", icon: User },
        { name: "Promo Codes", href: "/admin/promo-codes", icon: Tag },
        {
          name: "Referrals & partner",
          href: "/admin/referrals-promos",
          icon: Handshake,
        },
        { name: "Alerts", href: "/admin/alerts", icon: Bell },
        { name: "Dependencies", href: "/admin/deps", icon: Network },
        { name: "Audit Log", href: "/admin/audit-log", icon: ScrollText },
      ]
    : [];

  const isAdminActive =
    location.pathname === "/admin" || location.pathname.startsWith("/admin/");
  useEffect(() => {
    if (isAdminActive) setAdminOpen(true);
  }, [isAdminActive]);

  const handleLogout = async () => {
    try {
      await apiFetch("/api/auth/revoke-all", {
        method: "POST",
      });
    } catch {
      // Revoke is best-effort; clear local session regardless
    }
    try {
      await apiFetch("/api/auth/clear-cookies", {
        method: "POST",
        skipRefresh: true,
      });
    } catch {
      // Cookie clear is best-effort after local sign-out
    }
    clearWebSession();
    resetCoachChatAvailabilityCache();
    window.location.href = "/login";
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div
        className={`flex shrink-0 ${headerDividerClass} ${shellClass} ${
          isCollapsed && !isMobile
            ? "flex-col items-center justify-center gap-1.5 py-2.5 px-2 min-h-[4.25rem]"
            : "h-16 px-4 items-center justify-between"
        }`}
      >
        {isMobile ? (
          <>
            <SidebarLogo mobile onClick={() => setMobileOpen(false)} />
            <button
              onClick={() => setMobileOpen(false)}
              className={`p-2.5 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center ${
                isDark
                  ? "text-stone-300 hover:text-white hover:bg-stone-700 active:bg-stone-600"
                  : "text-stone-600 hover:text-stone-900 hover:bg-stone-100 active:bg-stone-200"
              }`}
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>
          </>
        ) : isCollapsed ? (
          <>
            <button
              type="button"
              onClick={() => setIsCollapsed(false)}
              className={`p-2 rounded-lg transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center ${
                isDark
                  ? "text-stone-400 hover:text-white hover:bg-stone-800"
                  : "text-stone-500 hover:text-stone-900 hover:bg-stone-100"
              }`}
              aria-label="Expand sidebar"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <SidebarLogo />
            <button
              onClick={() => setIsCollapsed(true)}
              className={`p-2.5 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0 ${
                isDark
                  ? "text-stone-400 hover:text-white hover:bg-stone-800"
                  : "text-stone-500 hover:text-stone-900 hover:bg-stone-100"
              }`}
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* Nav — grouped sections */}
      <nav className="flex-1 py-4 px-3 space-y-3 overflow-y-auto">
        {navSections.map((section) => {
          const showHeader = showSectionLabels && Boolean(section.label);
          const isOpen = showHeader ? sectionOpen[section.id] !== false : true;
          const hasActive = section.items.some(
            (item) =>
              location.pathname === item.href ||
              (item.href !== "/" && location.pathname.startsWith(`${item.href}/`))
          );

          return (
            <div key={section.id}>
              {showHeader ? (
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  className={`flex w-full items-center gap-2 px-3 py-2 rounded-lg min-h-[44px] text-left transition-colors ${
                    hasActive ? sectionHeaderActiveClass : "text-stone-500 hover:text-stone-400"
                  }`}
                  aria-expanded={isOpen}
                  aria-controls={`nav-section-${section.id}`}
                >
                  <span className="flex-1 text-[11px] font-semibold uppercase tracking-[0.12em]">
                    {section.label}
                  </span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    aria-hidden
                  />
                </button>
              ) : null}
              {isOpen ? (
                <div id={`nav-section-${section.id}`} className="space-y-1">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.href;
                    const showProBadge = item.proFeature && !isPro;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-3 py-2.5 rounded-lg font-medium transition-colors min-h-[44px] ${
                          !isMobile && isCollapsed ? "justify-center" : "gap-3"
                        } ${isActive ? navActiveClass : navTextClass}`}
                      >
                        <item.icon className="w-5 h-5 shrink-0" />
                        {showSectionLabels && (
                          <span className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
                            {item.name}
                            {showProBadge && <Badge variant="pro">PRO</Badge>}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}

        {/* Admin HQ (collapsible sub-nav) */}
        {adminNavigation.length > 0 && (
          <div className={`pt-2 mt-2 ${sectionDividerClass}`}>
            <button
              onClick={() => setAdminOpen(!adminOpen)}
              className={`flex items-center px-3 py-2.5 rounded-lg font-medium transition-colors min-h-[44px] w-full text-left ${
                !isMobile && isCollapsed ? "justify-center" : "gap-3"
              } ${isAdminActive ? sectionHeaderActiveClass : navTextClass}`}
              aria-expanded={adminOpen}
              aria-controls="admin-nav"
            >
              <Shield className="w-5 h-5 shrink-0" />
              {(isMobile || !isCollapsed) && (
                <span className="flex items-center gap-2 flex-1 whitespace-nowrap overflow-hidden">
                  Admin HQ
                  <ChevronDown
                    className={`w-3.5 h-3.5 ml-auto text-stone-500 transition-transform duration-200 ${
                      adminOpen ? "rotate-180" : ""
                    }`}
                  />
                </span>
              )}
            </button>
            {adminOpen && (isMobile || !isCollapsed) && (
              <div id="admin-nav" className="ml-2 mt-0.5 space-y-0.5">
                {adminNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[40px] ${
                        isActive ? navActiveClass : subNavTextClass
                      }`}
                    >
                      <item.icon className="w-4 h-4 shrink-0" />
                      <span className="whitespace-nowrap overflow-hidden">
                        {item.name}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
            {!isMobile && isCollapsed && (
              <div className="mt-0.5 space-y-0.5">
                {adminNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      title={item.name}
                      className={`flex items-center justify-center px-3 py-2 rounded-lg transition-colors min-h-[40px] ${
                        isActive ? navActiveClass : subNavTextClass
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </nav>

      {/* Footer — Account */}
      <div className={`p-3 ${sectionDividerClass} space-y-1`} style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}>
        {showSectionLabels ? (
          <button
            type="button"
            onClick={() => toggleSection("account")}
            className={`flex w-full items-center gap-2 px-3 py-2 rounded-lg min-h-[44px] text-left transition-colors ${
              location.pathname === "/profile" || location.pathname === "/settings"
                ? sectionHeaderActiveClass
                : "text-stone-500 hover:text-stone-400"
            }`}
            aria-expanded={sectionOpen.account !== false}
            aria-controls="nav-section-account"
          >
            <span className="flex-1 text-[11px] font-semibold uppercase tracking-[0.12em]">
              Account
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${
                sectionOpen.account !== false ? "rotate-180" : ""
              }`}
              aria-hidden
            />
          </button>
        ) : null}
        {(useSectionCollapse ? sectionOpen.account !== false : true) ? (
          <div id="nav-section-account" className="space-y-1">
            {(showProMemberChrome || showLaunchAccessChrome) && showSectionLabels && (
              <div
                data-testid="sidebar-plan-chrome"
                className={`mb-2 px-3 py-2 rounded-lg ring-1 w-full max-w-full min-w-0 box-border ${
                  isDark
                    ? "bg-gradient-to-r from-amber-500/10 to-amber-500/10 ring-amber-500/25"
                    : "bg-primary-100 ring-primary-500/20"
                }`}
              >
                {showProMemberChrome ? (
                  <>
                    <div className="flex items-center gap-2">
                      <Crown className={`w-4 h-4 ${isDark ? "text-amber-400" : "text-primary-800"}`} />
                      <span className={`text-sm font-semibold ${isDark ? "text-amber-400" : "text-primary-900"}`}>
                        Pro Member
                      </span>
                    </div>
                    <p className={`text-xs mt-0.5 ${isDark ? "text-stone-400" : "text-stone-700"}`}>Unlimited access</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${isDark ? "text-amber-400" : "text-primary-900"}`}>
                        {LAUNCH_PLAN_TITLE}
                      </span>
                    </div>
                    <p className={`text-xs mt-0.5 ${isDark ? "text-stone-400" : "text-stone-700"}`}>
                      {LAUNCH_PLAN_NOTE}
                    </p>
                  </>
                )}
              </div>
            )}
            <Link
              to="/profile"
              className={`flex items-center px-3 py-2.5 rounded-lg transition-colors min-h-[44px] ${
                !isMobile && isCollapsed ? "justify-center" : "gap-3"
              } ${navTextClass}`}
            >
              <div className="relative">
                <User className="w-5 h-5 shrink-0" />
                {showProMemberChrome && (
                  <Crown className="w-2.5 h-2.5 text-amber-500 absolute -top-1 -right-1" />
                )}
              </div>
              {showSectionLabels && (
                <span className="whitespace-nowrap overflow-hidden">Profile</span>
              )}
            </Link>
            <Link
              to="/settings"
              className={`flex items-center px-3 py-2.5 rounded-lg transition-colors min-h-[44px] ${
                !isMobile && isCollapsed ? "justify-center" : "gap-3"
              } ${navTextClass}`}
            >
              <Settings className="w-5 h-5 shrink-0" />
              {showSectionLabels && (
                <span className="whitespace-nowrap overflow-hidden">Settings</span>
              )}
            </Link>
            <button
              onClick={handleLogout}
              className={`flex items-center px-3 py-2.5 rounded-lg transition-colors w-full text-left min-h-[44px] ${
                !isMobile && isCollapsed ? "justify-center" : "gap-3"
              } ${navTextClass}`}
            >
              <LogOut className="w-5 h-5 shrink-0" />
              {showSectionLabels && (
                <span className="whitespace-nowrap overflow-hidden">Logout</span>
              )}
            </button>
          </div>
        ) : null}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <>
        {/* Stay mounted while the drawer is open so safe-area chrome does not unmount/jitter. */}
        <header
          className={`md:hidden fixed top-0 left-0 right-0 z-30 instrument-press-surface ${APP_MOBILE_HEADER_SAFE_AREA_CLASS} ${topBarClass}`}
          aria-hidden={mobileOpen || undefined}
          {...(mobileOpen ? { inert: '' } : {})}
        >
          <div className="h-14 flex items-center px-4 gap-3">
            <button
              onClick={openMobileNav}
              className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg ${
                isDark
                  ? "text-stone-400 hover:text-white hover:bg-stone-800 active:bg-stone-700"
                  : "text-stone-600 hover:text-stone-900 hover:bg-stone-100 active:bg-stone-200"
              }`}
              style={{ touchAction: "manipulation" }}
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            <SidebarLogo mobile />
          </div>
        </header>
        <AnimatePresence>
          {mobileOpen && (
            <>
              {/* Solid backdrop - no blur. Opening tap is ignored via WEB_NAV_OPEN_IGNORE_MS. */}
              <div
                key="sidebar-backdrop"
                className="fixed inset-0 bg-black/80 z-40 md:hidden cursor-pointer"
                role="button"
                tabIndex={-1}
                aria-label="Close menu"
                onClick={closeMobileNavFromBackdrop}
              />
              <motion.aside
                key="sidebar-drawer"
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                transition={{ type: "tween", duration: 0.2 }}
                className={`fixed left-0 w-[280px] max-w-[85vw] z-50 md:hidden instrument-press-surface ${shellClass}`}
                style={{
                  top: 0,
                  bottom: 0,
                  overflowY: "auto",
                  WebkitOverflowScrolling: "touch",
                  paddingTop: "env(safe-area-inset-top, 0px)",
                  paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)",
                }}
              >
                {sidebarContent}
              </motion.aside>
            </>
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <motion.aside
      initial={false}
      animate={{ width: isCollapsed ? 72 : 272 }}
      transition={{ type: "tween", duration: 0.2 }}
      className={`flex flex-col shrink-0 overflow-hidden min-w-0 instrument-press-surface ${shellClass}`}
      style={{ height: "100svh", maxHeight: "100svh" }}
    >
      {sidebarContent}
    </motion.aside>
  );
}

export default Sidebar;
